import React, { useState } from "react";
import Panel from "../../Common/UI/Panel";

enum TipColor {
  BadColor = "#FF5252",
  GoodColor = "#1EE2AC",
  MainKeywordColor = "#6C63FF",
}

type Tip = {
  label: string;
  content: string;
  color: TipColor;
  withPaddingLeft?: boolean;
};

type ProTipSectionProps = {
  children: JSX.Element[];
  footNote: string;
  header: string;
  id: string;
  intro: string;
};

const ProTip = ({ label, content, color, withPaddingLeft }: Tip) => {
  return (
    <div className={withPaddingLeft ? "pl-3" : ""}>
      <strong>
        <span style={{ color: color, lineHeight: "30px" }}>{label}</span>
        <span>{content}</span>
        <br />
      </strong>
    </div>
  );
};

const ProTipSection = ({
  children,
  footNote,
  header,
  id,
  intro,
}: ProTipSectionProps) => {
  const [collapsed, setCollapsed] = useState(true);
  const collapseClickHandler = () => {
    setCollapsed(!collapsed);
  };
  return (
    <div className="protip">
      <h4>{header}</h4>
      <div style={{ padding: 10 }}>
        <p style={{ paddingTop: 5 }}>{intro}</p>
        <div className="text-center pb-3">
          <a
            id={`pro_tips_toggle_${id}`}
            data-toggle="collapse"
            href={`#pro_tips_${id}`}
            aria-expanded="false"
            aria-controls="pro_tips"
            className="collapsed text-center"
            onClick={collapseClickHandler}
          >
            {collapsed ? "Click here to see examples" : "Collapse"}
          </a>
        </div>
        <div
          className="panel-body collapse"
          id={`pro_tips_${id}`}
          aria-expanded="false"
          style={{ height: 0 }}
        >
          {children}
          <p>{footNote}</p>
        </div>
      </div>
    </div>
  );
};

const ProTips = () => {
  const [collapsed, setCollapsed] = useState(true);
  const collapseClickHandler = () => {
    setCollapsed(!collapsed);
  };
  return (
    <Panel title={collapsed ? "" : "Pro Tips"}>
      <div
        className="iq-card-body"
        style={{ position: "relative", padding: 0 }}
      >
        <div className={`text-center ${collapsed ? "" : "pb-3"}`}>
          <div className="text-center">
            <a
              id="pro_tips_toggle_main"
              data-toggle="collapse"
              href="../../Util/components#pro_tips_main"
              aria-expanded="false"
              aria-controls="pro_tips"
              className="collapsed text-center"
              onClick={collapseClickHandler}
            >
              {collapsed ? "Click here for article creation tips" : "Collapse"}
            </a>
          </div>
        </div>
        <div
          id="pro_tips_main"
          className="collapse"
          aria-expanded="false"
          style={{ height: 0 }}
        >
          <p>
            When writing longer articles, it is important for Article Forge to
            understand <b>your intent</b> for the articles you want it to
            create.
          </p>
          <p>Use the formula below to create your main keywords:</p>
          <div
            className="col-lg-8"
            style={{
              maxWidth: 600,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <img
              className="rounded img-fluid mb-3 pl-3 pr-3"
              src="https://af.articleforge.com/images/Article-Forge-keyword-structure.png"
              alt="profile"
            />
          </div>
          <ProTipSection
            id="use_auto_section_headings"
            header="Using auto-generated section headings"
            intro="To create 1,500+ word articles, you need to choose a keyword that Article Forge can break down into smaller subtopics. So, it is important to make sure your main keywords are not too specific."
            footNote="Note: If you are writing shorter articles and are not using section headings, your keywords can be slightly more specific. For instance, “The Guggenheim Museum in New York City” might be too niche of a topic for a 1,500 word article with sections, but may make a great 250 word article without sections."
          >
            <ProTip
              label="Bad main keyword (too specific): "
              color={TipColor.BadColor}
              content="New York City weather in January"
            />
            <ProTip
              label="Bad main keyword (too specific): "
              color={TipColor.BadColor}
              content="Museum of Modern Art hours of operation"
            />
            <ProTip
              label="Good main keyword: "
              color={TipColor.GoodColor}
              content="What to do in New York City"
            />
            <ProTip
              label="Good main keyword: "
              color={TipColor.GoodColor}
              content="New York City museums"
            />
          </ProTipSection>
          <ProTipSection
            id="use_own_section_headings"
            header="Adding your own section headings"
            intro='When adding your own section headings, make sure each section heading is an "instance" or short "example" of your main keyword.'
            footNote='Note: The Guggenheim Museum is a great section heading for an article about what to do in New York City, but as mentioned above, it might be too specific as the main keyword for a 1,500 word article. In addition, when this topic is being used as a section heading, you do not need to add "in New York City" because the whole article will already be about New York City.'
          >
            <ProTip
              label="Main keyword: "
              color={TipColor.MainKeywordColor}
              content="What to do in New York City"
            />
            <ProTip
              label='Bad section heading (not example of "what to do"): '
              color={TipColor.BadColor}
              content="High cost of living"
              withPaddingLeft={true}
            />
            <ProTip
              label="Bad section heading (can be simplified): "
              color={TipColor.BadColor}
              content="Times Square is a place in New York City"
              withPaddingLeft={true}
            />
            <ProTip
              label="Good section headings: "
              color={TipColor.GoodColor}
              content="Times Square; Central Park; The MET; The Guggenheim Museum; The Empire State Building; Hudson Yards"
              withPaddingLeft={true}
            />
          </ProTipSection>
          <ProTipSection
            id="section_headings_intent"
            header="How intent impacts articles and section headings"
            intro="The intent of your main keyword will greatly impact the direction of your articles. When entering your own section headings, make sure they align with the overall intent of your main keyword."
            footNote='Note: Both "bad" section headings are structured well, they just do not match the intent of the main keywords.'
          >
            <ProTip
              label="Main keyword: "
              color={TipColor.MainKeywordColor}
              content="Pixel 6 features"
            />
            <ProTip
              label="Bad section heading: "
              color={TipColor.BadColor}
              content="iPhone"
              withPaddingLeft={true}
            />
            <ProTip
              label="Good section headings: "
              color={TipColor.GoodColor}
              content="AI-powered camera; Google Tensor; Live Translate; Music recognition; Battery life"
              withPaddingLeft={true}
            />
            <ProTip
              label="Main keyword: "
              color={TipColor.MainKeywordColor}
              content="Pixel 6 competitors"
            />
            <ProTip
              label="Bad section heading: "
              color={TipColor.BadColor}
              content="AI-powered camera"
              withPaddingLeft={true}
            />
            <ProTip
              label="Good section headings: "
              color={TipColor.GoodColor}
              content="iPhone 13; Samsung s22; OnePlus 9"
              withPaddingLeft={true}
            />
          </ProTipSection>
        </div>
      </div>
    </Panel>
  );
};

export default ProTips;
